import { useState, type FormEvent } from 'react';
import { ApiError } from '../../api.js';
import { useOffline } from '../../offline/context.js';
import { openStore } from '../../offline/local.js';
import { useSignedIn } from '../../session.js';
import {
  checkKioskPin,
  checkPasswordOffline,
  exitWithPassword,
  keptKioskPin,
  queueKioskExit,
  refreshKioskPin,
  type ExitMode,
} from '../../offline/kioskPin.js';

const until = (time: number) =>
  new Date(time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Staff only (prototype: .k-pin): the kiosk PIN, typed on a keypad, checked
 * on the device so it works without network. Used by Hand the tablet, then
 * by the booth kiosk. The texts stay in English: they are for the seller.
 * With network, a PIN missing or not matching is fetched again first (one
 * set a moment ago in Settings works at once). Without any PIN on the
 * device, the seller signed in leaves with their password (round 2).
 */
export function PinPad({
  digits,
  mode,
  onUnlock,
  onBack,
}: {
  digits: number;
  mode: ExitMode;
  onUnlock(): void;
  onBack(): void;
}) {
  const { store: opened } = useOffline();
  const { user } = useSignedIn();
  // The device's storage, even while it is still opening (a PIN typed at once).
  const storage = () => (opened ? Promise.resolve(opened) : openStore(user.id));
  const [pin, setPin] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [noPin, setNoPin] = useState(false);
  const [password, setPassword] = useState('');

  async function check(typed: string) {
    setChecking(true);
    try {
      const store = await storage();
      let { result, lockedUntil } = await checkKioskPin(store, typed);
      // Not on this device, or changed meanwhile: ask the portal once.
      if ((result === 'not_set' || result === 'wrong') && navigator.onLine) {
        const before = JSON.stringify(await keptKioskPin(store));
        await refreshKioskPin(store);
        if (JSON.stringify(await keptKioskPin(store)) !== before) {
          ({ result, lockedUntil } = await checkKioskPin(store, typed));
        }
      }
      if (result === 'ok') return onUnlock();
      setPin('');
      if (result === 'not_set') {
        setNoPin(true);
        setMessage(null);
        return;
      }
      setMessage(
        result === 'locked'
          ? `Too many wrong PINs. Try again after ${until(lockedUntil!)}.`
          : 'Wrong PIN. Try again.',
      );
    } catch {
      setPin('');
      setMessage('This device could not check the PIN. Try again.');
    } finally {
      setChecking(false);
    }
  }

  async function leaveWithPassword(e: FormEvent) {
    e.preventDefault();
    if (!password || checking) return;
    setChecking(true);
    setMessage(null);
    try {
      const store = await storage();
      if (navigator.onLine) {
        try {
          await exitWithPassword(store, password, mode);
          return onUnlock();
        } catch (error) {
          if (!(error instanceof ApiError) || error.code !== 'offline') {
            setPassword('');
            setMessage(
              error instanceof ApiError && error.code === 'rate_limited'
                ? 'Too many attempts. Wait a few minutes, then try again.'
                : error instanceof ApiError && error.code === 'invalid_credentials'
                  ? 'Wrong password, or the account is locked for a while. Try again.'
                  : 'The portal could not check it. Try again.',
            );
            return;
          }
        }
      }
      // Without network: against the hash kept since the sign-in.
      const { result, lockedUntil } = await checkPasswordOffline(store, password);
      setPassword('');
      if (result === 'ok') {
        await queueKioskExit(store, mode);
        return onUnlock();
      }
      setMessage(
        result === 'locked'
          ? `Too many wrong attempts. Try again after ${until(lockedUntil!)}.`
          : result === 'no_check'
            ? 'Without network, this device cannot check your password yet. Connect to the network once, then try again.'
            : 'Wrong password. Try again.',
      );
    } finally {
      setChecking(false);
    }
  }

  const key = (k: string) => {
    if (checking) return;
    setMessage(null);
    const next = k === '⌫' ? pin.slice(0, -1) : pin.length < digits ? pin + k : pin;
    setPin(next);
    if (next.length === digits && k !== '⌫') void check(next);
  };

  if (noPin) {
    return (
      <form className="k-pin" onSubmit={(e) => void leaveWithPassword(e)}>
        <h2>Staff only</h2>
        <p>
          No kiosk PIN on this device yet (an admin sets it in Settings). Enter your password to
          leave.
        </p>
        <input
          className="k-pw"
          type="password"
          autoComplete="current-password"
          aria-label="Your password"
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" className="btn primary" disabled={!password || checking}>
          Leave
        </button>
        <p className="k-err" role="alert">
          {message}
        </p>
        <button type="button" className="linkbtn" onClick={onBack}>
          Back
        </button>
      </form>
    );
  }

  return (
    <div className="k-pin">
      <h2>Staff only</h2>
      <p>Enter the PIN to leave kiosk mode.</p>
      <div className="k-dots" aria-label={`${pin.length} of ${digits} digits typed`}>
        {Array.from({ length: digits }, (_, i) => (
          <i key={i} className={i < pin.length ? 'on' : ''} />
        ))}
      </div>
      <div className="k-pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => (
          <button key={k} type="button" onClick={() => key(k)}>
            {k}
          </button>
        ))}
        <span />
        <button type="button" onClick={() => key('0')}>
          0
        </button>
        <button type="button" aria-label="Delete the last digit" onClick={() => key('⌫')}>
          ⌫
        </button>
      </div>
      <p className="k-err" role="alert">
        {message}
      </p>
      <button type="button" className="linkbtn" onClick={onBack}>
        Back
      </button>
    </div>
  );
}
